import { EvaluationCase, EvaluationRowResult, EvaluationRowState } from './types';
import { declarationKey, RunTone } from './utils';

/**
 * One line of the evaluation table: a case, and what the last run made of it.
 *
 * The two are matched on the **declaration**, which the API keeps unique, and
 * never on position — `results[].index` is the position the case had when the
 * run started, so a case added, removed or reordered since would pair a
 * declaration with somebody else's verdict. That join is what used to crash
 * the page; here a case that no run has seen simply has no result, and a
 * result whose case is gone keeps its own row rather than being dropped.
 */
export type EvaluationTableRow = {
    /** stable across re-renders, so an open row stays open */
    key: string;
    /** position in the case list, or null for a case that no longer exists */
    caseIndex: number | null;
    declaration: Record<string, any>;
    /** the verdict asked for now — or the one the run was given, for a case that is gone */
    expected: boolean;
    result: EvaluationRowResult | null;
    /** the run graded this declaration against a different verdict than the case asks for now */
    stale: boolean;
    /** the run graded a declaration the case list no longer has */
    orphan: boolean;
};

/** the cases and the run, side by side, in case order with the leftovers after */
export const mergeCases = (
    cases: EvaluationCase[],
    results: EvaluationRowResult[],
): EvaluationTableRow[] => {
    const byDeclaration = new Map<string, EvaluationRowResult>();
    results.forEach((result) => {
        const key = declarationKey(result.declaration);
        if (!byDeclaration.has(key)) byDeclaration.set(key, result);
    });

    const matched = new Set<string>();
    const rows: EvaluationTableRow[] = cases.map((item, index) => {
        const key = declarationKey(item.declaration);
        const result = byDeclaration.get(key) ?? null;
        if (result) matched.add(key);
        return {
            key: `case-${index}`,
            caseIndex: index,
            declaration: item.declaration,
            expected: item.is_valid,
            result,
            stale: !!result && result.expected !== item.is_valid,
            orphan: false,
        };
    });

    results.forEach((result) => {
        if (matched.has(declarationKey(result.declaration))) return;
        rows.push({
            key: `run-${result.response_id}`,
            caseIndex: null,
            declaration: result.declaration,
            expected: result.expected,
            result,
            stale: false,
            orphan: true,
        });
    });

    return rows;
};

/**
 * What kind of thing the status is.
 *
 * `response` means the row has not got past the call itself — queued, in
 * flight, or failed — and those are `AIProcessorResponse` states, which the
 * app already has one tag for. The other two are about the *comparison*, which
 * only a call that came back can have.
 */
export type RowStatusKind = 'not-run' | 'response' | 'passed' | 'mismatch';

export type RowStatus = {
    kind: RowStatusKind;
    text: string;
    tone: RunTone;
    /** the AI processor response state, when that is what the status is */
    state?: EvaluationRowState;
};

/**
 * Where the row stands, in one word.
 *
 * A row still in flight is neither a pass nor a fail; a row the model answered
 * with something that was not a verdict is a failed call rather than a
 * disagreement. Both used to render as "failed".
 */
export const rowStatus = (row: EvaluationTableRow): RowStatus => {
    const { result } = row;
    if (!result) return { kind: 'not-run', text: 'Not run', tone: 'idle' };
    if (result.state === 'SCHEDULED' || result.state === 'WAITING_FOR_RESPONSE') {
        return { kind: 'response', text: 'Waiting', tone: 'running', state: result.state };
    }
    if (result.state === 'FAILED') {
        return { kind: 'response', text: 'Failed', tone: 'bad', state: 'FAILED' };
    }
    if (result.passed === true) return { kind: 'passed', text: 'Passed', tone: 'good' };
    if (result.passed === false) return { kind: 'mismatch', text: 'Mismatch', tone: 'bad' };
    return { kind: 'response', text: 'Waiting', tone: 'running', state: 'WAITING_FOR_RESPONSE' };
};

/** what is out of step between this row's case and the run, if anything */
export const rowConflict = (row: EvaluationTableRow): string | null => {
    if (row.orphan) return 'Deleted from the cases since this run';
    if (row.stale) return 'The expected verdict changed after this run';
    return null;
};

/** whether anything on screen no longer matches the saved cases */
export const hasConflicts = (rows: EvaluationTableRow[]) => rows.some((row) => !!rowConflict(row));
